/**
 * Shared API client
 * Centralizes auth header injection and Pattern A ({success, data}) response
 * handling so individual services don't each re-implement the same fetch
 * boilerplate. Endpoints with other response shapes (search, properties,
 * featured, etc.) should call `request()` and parse the raw body themselves.
 */

import { API_CONSTANTS } from '../Constants';

export const BASE_URL = API_CONSTANTS.BASE_URL;

export const getAuthToken = () => {
    return localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
};

export const buildHeaders = ({ multipart = false } = {}) => {
    const headers = { 'Authorization': `Bearer ${getAuthToken()}` };
    if (!multipart) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

/**
 * Fetch wrapper for Pattern A endpoints. Returns the parsed envelope
 * `{success, data}` on success; throws on both non-2xx responses and on
 * 200-with-success:false, with `status` and `field` attached to the error
 * so callers can branch on them (401 vs validation vs generic failure).
 */
export const request = async (path, { method = 'GET', body, multipart = false } = {}) => {
    const response = await fetch(`${BASE_URL}${path}`, {
        method,
        headers: buildHeaders({ multipart }),
        ...(body ? { body: multipart ? body : JSON.stringify(body) } : {})
    });

    const data = await response.json();

    if (!response.ok) {
        const error = new Error(
            response.status === 401
                ? 'Session expired. Please login again.'
                : (data.error || data.message || `HTTP error! status: ${response.status}`)
        );
        error.status = response.status;
        error.field = data.field;
        throw error;
    }

    if (!data.success) {
        const error = new Error(data.error || data.message || 'Request failed');
        error.status = response.status;
        error.field = data.field;
        throw error;
    }

    return data;
};

export const unwrapPatternA = (data) => data.data;

/**
 * Fetch wrapper for endpoints that don't use the {success, data} envelope at
 * all (Pattern B/D - raw response bodies like /search, /search/advanced,
 * /properties/{id}, /featured/*). Only checks HTTP status, since there's no
 * `success` field to inspect.
 */
export const rawRequest = async (path, { method = 'GET', body, multipart = false } = {}) => {
    const response = await fetch(`${BASE_URL}${path}`, {
        method,
        headers: buildHeaders({ multipart }),
        ...(body ? { body: multipart ? body : JSON.stringify(body) } : {})
    });

    const data = await response.json();

    if (!response.ok) {
        const error = new Error(data.error || data.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        throw error;
    }

    return data;
};
